import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'

/**
 * How the dashboard print route (AB#6645) sends its reads (AB#6933).
 *
 * A printed dashboard asks for everything at once: a weekly table over three
 * months and a weekly chart over six are ~40 full-week rollup evaluations
 * fired in the same instant. Each one is cheap alone; all of them together
 * queue up behind each other in core until some pass the renderer's per-call
 * timeout and the week shows "!". Interactive dashboards load the same way,
 * but there a slow week fills in a moment later; a PDF is printed once.
 *
 * So on the print route:
 * - at most PRINT_MAX_IN_FLIGHT reads are in flight; the rest wait their turn
 *   (they count as pending, so the page does not report ready meanwhile);
 * - a read that failed for a reason that may pass (network/abort, timeout, 429, 5xx)
 *   is sent once more after PRINT_RETRY_DELAY_MS;
 * - a read that still fails is recorded, so the page can say it is incomplete
 *   instead of the renderer printing a silently wrong PDF.
 */
export const PRINT_MAX_IN_FLIGHT = 4
export const PRINT_RETRY_DELAY_MS = 1000

let inFlight = 0
const waiting: Array<() => void> = []
const failedReads = new Set<string>()

const acquire = async (): Promise<void> => {
  if (inFlight < PRINT_MAX_IN_FLIGHT) {
    inFlight += 1
    return
  }
  // The slot is handed over by release(), so inFlight stays counted.
  await new Promise<void>((resolve) => waiting.push(resolve))
}

const release = (): void => {
  const next = waiting.shift()
  if (next) next()
  else inFlight -= 1
}

/** Whether a failed read may succeed when sent again. 4xx other than 429 are final answers. */
export const isTransientError = (error: unknown): boolean => {
  const status = (error as FetchBaseQueryError | undefined)?.status
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 429) return true
  return typeof status === 'number' && status >= 500
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

type QueryResult = {data?: unknown; error?: unknown}

/**
 * Run one print-route read: queued behind PRINT_MAX_IN_FLIGHT, retried once on
 * a transient failure, and recorded under `key` if it still fails.
 */
export const runPrintRead = async <R extends QueryResult>(
  key: string,
  send: () => R | Promise<R>,
): Promise<R> => {
  await acquire()
  try {
    let result = await send()
    if (result.error && isTransientError(result.error)) {
      await sleep(PRINT_RETRY_DELAY_MS)
      result = await send()
    }
    if (result.error && isTransientError(result.error)) failedReads.add(key)
    else failedReads.delete(key)
    return result
  } finally {
    release()
  }
}

/** The reads that still failed after their retry, e.g. `GET signalrollups/with_value/`. */
export const getFailedPrintReads = (): string[] => [...failedReads]

/** Test hook: forget the queue and the recorded failures. */
export const resetPrintReads = (): void => {
  inFlight = 0
  waiting.length = 0
  failedReads.clear()
}
